import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import type { Batch } from '../types/publish.types';

interface BatchActionDialogProps {
  batch: Batch | null;
  title: string;
  description: string;
  confirmLabel: string;
  commentRequired?: boolean;
  destructive?: boolean;
  isPending: boolean;
  onConfirm: (comments: string) => void;
  onOpenChange: (open: boolean) => void;
}

export function BatchActionDialog({
  batch,
  title,
  description,
  confirmLabel,
  commentRequired = false,
  destructive = false,
  isPending,
  onConfirm,
  onOpenChange,
}: BatchActionDialogProps) {
  const key = batch?.batchId ?? null;
  const [draft, setDraft] = useState<{ key: number | null; text: string }>({
    key,
    text: '',
  });
  const comments = draft.key === key ? draft.text : '';

  const blocked = commentRequired && comments.trim() === '';

  return (
    <Dialog open={batch !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div>
          <p className="mb-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
            Comment{commentRequired ? '' : ' (optional)'}
          </p>
          <Input
            value={comments}
            onChange={(event) => setDraft({ key, text: event.target.value })}
            placeholder="Why, in a line"
            autoComplete="off"
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={destructive ? 'destructive' : 'default'}
            disabled={blocked || isPending}
            onClick={() => onConfirm(comments.trim())}
          >
            {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
